import { createReportEventUnauthenticatedParams, ReportEventUnauthenticatedParams ,TelemetryForwarder, ValidPropertyValueType } from '@tableau/typescript-telemetry-forwarder';
import * as _ from 'underscore';
import * as uuidv4 from 'uuid/v4';

import { TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { UserAgentSupport } from '../../common/utilities/UserAgentSupport';
import { Module } from '../Module';
import { ServerService } from './ServerService';
import { SessionService } from './SessionService';

export type BaseTelemetryEvent = { type: TelemetryEvents };
export type AnalyticsEventProperties = { [K: string]: ValidPropertyValueType };

/**
 * Only specific types (validPropertyValueType) are allowed as values for events.
 * Custom telemetry event interfaces can extend this type to ensure that type-safety.
 */
type ValidTelemetryEventInternal<T extends BaseTelemetryEvent, U extends keyof T> = {
  [K in U]: T[K] extends ValidPropertyValueType ? T[K] : never
};
export type ValidTelemetryEvent<T extends BaseTelemetryEvent> = ValidTelemetryEventInternal<T, keyof T>;

/**
 * This interface supports lighter dependencies for components that don't want to inherit angular services (eg. React).
 *
 * function example(telemetry: Telemetry): void {
 *    telemetry.sendEvent({
 *      type: TelemetryEvents.yourEvent,
 *      extra1: 'property1'
 *    });
 * }
 */
export interface Telemetry {
  sendEvent<T extends BaseTelemetryEvent>(event: ValidTelemetryEvent<T>): void;
}

/**
 * TelemetryService handles sending telemetry events to the Google Big Query database.
 *
 * App-wide event properties get set through the reporter and eventFactory
 * Usage examples: UrlTelemetryService.ts for Angular and SiteSpace.tsx for React
 *
 * For setting up events in Google BigQuery, see https://mytableau.tableaucorp.com/x/0hOHBw
 */
export class TelemetryService implements Telemetry {

  public static $inject = [
    'ServerService',
    'SessionService'
  ];

  private telemetryReporter: TelemetryForwarder;

  constructor(private ServerService: ServerService, private SessionService: SessionService)
  {
    this.telemetryReporter = this.getForwarder();
  }

  public getService(ServerService: ServerService, SessionService: SessionService)
  {
    return new TelemetryService(ServerService, SessionService);
  }

  // This method can't be inlined, because it's stubbed in the unit tests
  public getForwarder() {
    if(!this.telemetryReporter){
      return new TelemetryForwarder();
    }
    return this.telemetryReporter;
  }

  /**
   * @param event Must look like { type: TelemetryEvents.yourEvent, foo: 'bar', ex: 'ample' }
   */
  public sendEvent<T extends BaseTelemetryEvent>(event: ValidTelemetryEvent<T>): void {
    const { type, ...customProps } = event as BaseTelemetryEvent & AnalyticsEventProperties;

    const properties: AnalyticsEventProperties = {
      ...customProps,
      ...this.getCommonProperties()
    };

    Object.keys(properties).forEach(key => {
      // since remove waitingForSession or getSessionInfo before we send telemetry, values of sessionInfo could be null
      // vizportal java code does not allow null value, so need convert to empty string
      if(properties[key] === undefined || properties[key] === null){
        properties[key] = '';
      }
    });

    // java-event-client use NetworkUtilities.getHostName() to get hostName, so we do not need to add hostName
    // https://gitlab.tableausoftware.com/Product-Analytics/java-event-client/blob/master/src/main/java/com/tableau/telemetry/event/AEvent.java#L27
    const params: ReportEventUnauthenticatedParams = createReportEventUnauthenticatedParams(type,'vizportalclient' ,properties);
    this.telemetryReporter.send(params);
  }

  public getCommonProperties(): AnalyticsEventProperties {
    return {
      uuid: uuidv4(),
      user_id: this.ServerService.sessionUserId(),
      site_id: this.ServerService.sessionSiteLuid(),
      session_id: this.SessionService.getSessionId(),
      is_mobile: UserAgentSupport.isMobileBrowser()
    };
  }
}

Module.instance().service('TelemetryService', TelemetryService);
